package com.elan.edubridge.shared.domain.model

import kotlinx.serialization.Serializable

/**
 * 출결 상태(§3.4-7).
 *
 * 문자열 값은 `functions/`의 TS와 반드시 동일하다:
 * NOT_ARRIVED | PRESENT | LATE | ABSENT | LEFT.
 */
@Serializable
enum class AttendanceStatus(val displayName: String) {
    /** 로스터 선생성 직후의 초기값(§S2 materializeDailyRoster). */
    NOT_ARRIVED("미등원"),
    PRESENT("출석"),
    LATE("지각"),
    ABSENT("결석"),
    LEFT("하원"),
}

/**
 * 이벤트 종류. 정정·취소도 삭제가 아니라 [AMEND] append다(§3.5).
 *
 * 문자열 값은 TS와 동일하다: CHECK_IN | CHECK_OUT | AMEND.
 */
@Serializable
enum class AttendanceEventType { CHECK_IN, CHECK_OUT, AMEND }

/** 출결 발생 경로. 선생님 수동 처리는 항상 [MANUAL]로 남아 감사된다(§3.7). */
@Serializable
/**
 * [ROSTER]는 04:00 KST 스케줄러가 미리 만든 `NOT_ARRIVED` 행이다. 이벤트가 없는
 * 유일한 출처이며(등원하지 않은 것은 사건이 아니다), append-only를 깨지 않는다(§7 S2).
 */
enum class AttendanceSource { QR, MANUAL, ROSTER }

/**
 * 진실의 원천. `edubridge_academies/{academyId}/attendanceEvents/{eventId}`.
 *
 * **APPEND ONLY이고 불변이다**(§3.5). 클라이언트는 읽기만 하고 갱신·삭제 경로가 없다.
 * `nonce` 원문이 아니라 [nonceHash]를 남긴다 — 감사 로그가 재사용 가능한 비밀을 보관하지 않게 한다.
 */
@Serializable
data class AttendanceEvent(
    val eventId: String,
    val academyId: String,
    val studentId: String,
    val studentName: String,
    /** 귀속 반이 정해지지 않으면 null — 학원 단위 등하원이다(§3.4-8). */
    val classId: String? = null,
    val className: String? = null,
    val type: AttendanceEventType,
    val status: AttendanceStatus,
    /** ISO-8601 서버 시각. */
    val occurredAt: String,
    /** 학원 timeZone 기준 `yyyy-MM-dd`. 일별 집계는 이 문자열 등호 쿼리만 쓴다(R12). */
    val serviceDate: String,
    val source: AttendanceSource,
    /** 이벤트를 발생시킨 계정. QR 스캔이면 학생 본인, 수동 처리면 스태프. */
    val actorUid: String? = null,
    val sessionId: String? = null,
    /** nonce 원문이 아닌 SHA-256 해시(§3.5). */
    val nonceHash: String? = null,
    /** AMEND가 정정하는 대상 이벤트. */
    val previousEventId: String? = null,
)

/**
 * 개인정보 미러. `edubridge_users/{uid}/attendanceTimeline/{eventId}`(§4.3).
 *
 * **격리가 경로로 보장된다** — 다른 학생의 문서는 애초에 접근 가능한 경로에 존재하지 않는다.
 * 쿼리 제약에 의존하지 않으므로 클라이언트가 조건을 빠뜨려도 남의 데이터가 나올 수 없다.
 * 학생 본인과 ACTIVE 링크 보호자 전원에게 팬아웃되고, 링크 해지 시 트리거가 삭제한다(§4.4).
 */
@Serializable
data class AttendanceTimelineEntry(
    val eventId: String,
    /** 학부모의 다중 자녀를 구분하는 축. */
    val subjectStudentId: String,
    val subjectStudentName: String,
    val academyId: String,
    val academyName: String,
    val classId: String? = null,
    val className: String? = null,
    val status: AttendanceStatus,
    val occurredAt: String,
    val serviceDate: String,
    val source: AttendanceSource,
)

/** v4 대시보드 통계 4타일(`수업 8 / 등원 42 / 하원 31 / 미등원 7`). */
@Serializable
data class AttendanceSummary(
    val academyId: String,
    val serviceDate: String,
    val classCount: Int = 0,
    val checkedInCount: Int = 0,
    val checkedOutCount: Int = 0,
    val notArrivedCount: Int = 0,
)

/**
 * 파생 프로젝션. `edubridge_academies/{academyId}/attendanceRecords/{serviceDate}_{classId}_{studentId}`.
 *
 * "기록 수정"이 아니라 "로그를 접은 결과"다 — [AttendanceEvent]만으로 재구성 가능해야 한다(§3.5).
 * 통계·모바일 미처리 카운트·실시간 피드·월별 이력이 전부 이것에 의존한다.
 */
@Serializable
data class DailyRosterEntry(
    val academyId: String,
    val serviceDate: String,
    val classId: String,
    val className: String,
    val studentId: String,
    val studentName: String,
    val status: AttendanceStatus,
    /**
     * 이 건에서 **지각으로 등원했는가**. [status]와 직교하는 별도 축이다.
     *
     * [status]는 최신 상태 하나뿐이라 지각 등원 후 하원하면 `LEFT`로 접혀 지각 이력이 사라진다
     * ([AttendanceTransition.nextOnScan]: `LATE → LEFT`). 서버가 등원 시점에 한 번 true로 남기면
     * 이후 전이에도 유지하므로(`functions/src/qr.ts`·`attendance.ts`) `status == LEFT`와
     * `lateArrival == true`가 동시에 성립하는 것이 정상이다. 관리자 정정
     * (`amendAttendance`)만 이 값을 false로 되돌린다 — 정정은 "사실이 달랐다"는 뜻이다.
     */
    val lateArrival: Boolean = false,
    val lastEventId: String? = null,
    val lastEventAt: String? = null,
    /**
     * 등원 시각(ISO-8601). 아직 등원하지 않았으면 null이다.
     *
     * **[lastEventAt]과 별개인 이유**: [lastEventAt]은 최신 이벤트 하나의 시각이라 하원하는 순간
     * 등원 시각을 덮어쓴다. 화면은 등원·하원을 각각 `시:분`으로 나란히 보여줘야 하므로 두 축이
     * 따로 필요하다. 서버는 등원 시각을 **한 번만** 쓰고 이후 전이에도 유지한다
     * (`attendance.ts`·`qr.ts`: `existing.checkInAt ?? at`) — [lateArrival]과 같은 고착 규칙이다.
     */
    val checkInAt: String? = null,
    /** 하원 시각(ISO-8601). `CHECK_OUT` 이벤트에서만 채워지므로 등원만 한 상태면 null이다. */
    val checkOutAt: String? = null,
)

/**
 * 스캔 1회가 어떤 상태로 이어지는지의 전이표(§3.4-7).
 *
 * 서버가 진짜 판정 주체지만(클라이언트는 이 결과를 신뢰하지 않는다) 같은 규칙이 TS·Kotlin에
 * 두 벌 존재하므로 여기 단언 테스트를 두어 드리프트를 막는다.
 */
object AttendanceTransition {
    /**
     * @param current 해당 학원·`serviceDate`의 최신 상태. 기록이 없으면 null.
     * @param isLate 등원 판정 시 수업 시작 + `lateThresholdMinutes`를 넘겼는가.
     */
    fun nextOnScan(current: AttendanceStatus?, isLate: Boolean): AttendanceStatus = when (current) {
        AttendanceStatus.PRESENT, AttendanceStatus.LATE -> AttendanceStatus.LEFT
        else -> if (isLate) AttendanceStatus.LATE else AttendanceStatus.PRESENT
    }

    /** 전이 결과가 append할 이벤트 종류. AMEND는 스캔이 아니라 정정 경로에서만 나온다. */
    fun eventTypeFor(next: AttendanceStatus): AttendanceEventType =
        if (next == AttendanceStatus.LEFT) AttendanceEventType.CHECK_OUT else AttendanceEventType.CHECK_IN
}
